import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import { notAFitService, NotAFitRecord } from '../services/notAFitService';

// FEAT-042 — list the caller's own active Not a Fit hides with an undo
// button on each row (MVP-8.8 unhide path). Rendered inside SettingsPage.

interface NotAFitSettingsListProps {
  testIdPrefix?: string;
}

export const NotAFitSettingsList: React.FC<NotAFitSettingsListProps> = ({
  testIdPrefix = 'not-a-fit',
}) => {
  const [items, setItems] = useState<NotAFitRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const list = await notAFitService.listMyNotAFit();
      setItems(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleUndo = async (id: string) => {
    setPendingId(id);
    try {
      await notAFitService.undoNotAFit(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } finally {
      setPendingId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2} py={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading your hidden profiles…
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-empty`}>
        You haven&apos;t hidden any profiles. Anyone you mark &quot;Not a fit&quot; will
        appear here so you can undo it later.
      </Typography>
    );
  }

  return (
    <List data-testid={`${testIdPrefix}-list`}>
      {items.map((item) => (
        <ListItem key={item.id} data-testid={`${testIdPrefix}-row-${item.id}`}>
          <ListItemText
            primary={`Hidden user ${item.targetId}`}
            secondary={`Hidden on ${new Date(item.createdAt).toLocaleString()}`}
          />
          <ListItemSecondaryAction>
            <Button
              variant="outlined"
              size="small"
              disabled={pendingId === item.id}
              onClick={() => handleUndo(item.id)}
              data-testid={`${testIdPrefix}-undo-${item.id}`}
            >
              {pendingId === item.id ? 'Undoing…' : 'Undo'}
            </Button>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};

export default NotAFitSettingsList;
